import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Sparkles } from "lucide-react";
import {
  buyersApi,
  buyerSubmissionsApi,
  listingsApi,
  submissionAlreadyPresented,
  type BuyerLeadSummaryDto,
  type BuyerSubmissionPresentationDto,
  type CreateBuyerSubmissionPayload,
  type CreateListingPayload,
} from "@/api/buyers";
import { ApiError, extractErrorMessage } from "@/api/client";
import { Modal } from "@/components/ui/Modal";
import {
  Button,
  buttonVariants,
  Input,
  Skeleton,
  StatusBadge,
  StatusStepper,
  Textarea,
  useToast,
} from "@/design-system/components/ui";
import { calendarDateInputToIso, formatCurrency } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import { resolvedShortListingName } from "@/lib/listingDisplay";
import { buyerKeys, buyerSubmissionKeys, listingKeys } from "@/hooks/buyers/buyerKeys";
import { useSendBuyerPrequalInvite } from "@/hooks/buyers/usePrequalificationInvites";
import { useArchiveSubmission } from "@/hooks/buyers/useSubmissionArchive";
import { DuplicateInviteConfirm } from "../prequalification/inviteBits";
import {
  AgentFieldsBlock,
  agentFormErrors,
  agentFormFrom,
  agentHasIdentity,
  agentToPayload,
  EMPTY_AGENT_FORM,
  type AgentForm,
} from "../ListingAgentDirectory";
import {
  EMPTY_LISTING_FORM,
  ListingDuplicateWarning,
  ListingFieldsBlock,
  listingFormFrom,
  listingNumberErrors,
  listingToPayload,
  useListingDuplicates,
  type ListingForm,
} from "../ListingEditModal";
import { InlineQueryError } from "../queryStates";
import { BuyerPicker, ListingPicker, type PickedListing } from "./PresentBuyerPickers";
import { listingMatchKeys } from "./listingMatchKeys";
import {
  ALREADY_PRESENTED_BLURB,
  alreadyPresentedHint,
  alreadyPresentedReason,
  BUYER_STEP_BLURB,
  LISTING_REVIEW_BLURB,
  LISTING_STEP_BLURB,
  NDA_AUTHORISED,
  NDA_DOES_NOT_BLOCK,
  NDA_EXITS,
  NDA_NOT_AUTHORISED,
  NDA_SEND_CONFIRM,
  NDA_SEND_INTRO,
  NDA_SEND_SENT,
  NDA_STEP_BLURB,
  PASTE_STEP_BLURB,
  PRICE_SNAPSHOT_BLURB,
  presentSteps,
  TERMS_STEP_BLURB,
  WHAT_A_SUBMISSION_IS,
  WHAT_HAPPENS_NEXT,
  type PresentStepKey,
} from "./presentBuyerCopy";

/**
 * Present a buyer to a listing — the ONLY way a submission comes into being.
 *
 * <p>Replaces `CreateSubmissionModal`, which asked for two pickers and three
 * optional inputs on one screen with no explanation, could not be told which
 * buyer or listing the specialist was already looking at, and dead-ended on
 * "No listings match — try a different search". It is deleted rather than left
 * alongside: two ways to create the same record is how two surfaces end up
 * disagreeing about what one is.</p>
 *
 * <p><b>The ladder is DERIVED, not fixed</b> (`presentSteps`). A half that
 * arrived with the launch context is not asked for, and the create-a-listing
 * branch inserts its own two steps only once entered — so the stepper always
 * shows the honest number of decisions left.</p>
 *
 * <p>🔴 <b>An already-presented pair is REFUSED (ruling R1).</b> Any
 * non-deleted submission for the buyer + listing, whatever its status and
 * archived included, refuses a new one — the server's own check, mirrored here
 * from `duplicate-check` and backstopped by create's 409. There is no "present
 * anyway": the refusal names the standing submission and offers the one way
 * forward (RESTORE an archived one, OPEN any other).</p>
 *
 * <p>The buyer's NDA authorisation, by contrast, is explained and never a gate
 * (the server's gate is on recording a SIGNED NDA, not on creating a
 * submission).</p>
 */
export function PresentBuyerWizard({
  open,
  onClose,
  buyerLeadId,
  buyerName,
  listingId,
  listingName,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * Opened from a buyer's own record. LOCKS that half: the buyer is the context
   * the specialist arrived in, and its step is not drawn.
   */
  buyerLeadId?: string;
  /** Display hint while the record resolves — the NewDealWizard convention, so
   *  a caller holding only an id still works. */
  buyerName?: string | null;
  /** Opened against a known listing. Skipped like the buyer, but changeable:
   *  from a shortlist row the listing is a suggestion, not the context. */
  listingId?: string;
  listingName?: string | null;
  /**
   * 🔴 <b>Where the new submission is shown.</b> Four of the six mount points
   * have a RECORD underneath — a buyer, a listing — and navigating to the
   * submission's own route throws that record away along with the tab, the
   * scroll position and any drawer open over it. A host that can show the
   * submission in place says so here; the default stays the hard navigate,
   * which is right for `BuyerSubmissionsListPage`, where there is nothing
   * underneath to preserve. A RESTORE from the already-presented refusal lands
   * here too, with the restored submission's id.
   */
  onCreated?: (submissionId: string) => void;
}) {
  const qc = useQueryClient();
  const fmt = useDateFormatter();
  const toast = useToast();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [pickedBuyer, setPickedBuyer] = useState<BuyerLeadSummaryDto | null>(null);
  const [pickedListing, setPickedListing] = useState<PickedListing | null>(null);
  const [listingUnlocked, setListingUnlocked] = useState(false);
  const [creatingListing, setCreatingListing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Create's 409 — the race backstop to the duplicate-check refusal.
  const [conflict, setConflict] = useState<BuyerSubmissionPresentationDto | null>(null);

  // Terms.
  const [askingPrice, setAskingPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [followUp, setFollowUp] = useState("");

  // The create-a-listing branch.
  const [pasteText, setPasteText] = useState("");
  const [extractError, setExtractError] = useState<string | null>(null);
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);
  const [listingForm, setListingForm] = useState<ListingForm>(EMPTY_LISTING_FORM);
  const [agent, setAgent] = useState<AgentForm>(EMPTY_AGENT_FORM);
  const [hasAgent, setHasAgent] = useState(false);
  const [modelUsed, setModelUsed] = useState<string | null>(null);

  // Fresh run per open — the TransitionStartWizard shape, so a cancelled run
  // never leaves half a listing extraction sitting in the next one.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setPickedBuyer(null);
    setPickedListing(null);
    setListingUnlocked(false);
    setCreatingListing(false);
    setError(null);
    setConflict(null);
    setAskingPrice("");
    setNotes("");
    setFollowUp("");
    setPasteText("");
    setExtractError(null);
    setFallbackNote(null);
    setListingForm(EMPTY_LISTING_FORM);
    setAgent(EMPTY_AGENT_FORM);
    setHasAgent(false);
    setModelUsed(null);
  }, [open]);

  const buyerLocked = Boolean(buyerLeadId);
  const listingLocked = Boolean(listingId) && !listingUnlocked;

  const steps = presentSteps({ buyerLocked, listingLocked, creatingListing });
  // Clamped: the ladder grows and shrinks as the create-a-listing branch is
  // entered and left, and an index left pointing past the end would render no
  // step at all.
  const stepKey = steps[Math.min(step, steps.length - 1)].key as PresentStepKey;
  const listingStepIndex = buyerLocked ? 0 : 1;

  // ---- Prefill resolution ------------------------------------------------
  // Only the id is required of a caller; the record is read from the cache the
  // launching surface has usually already filled.
  //
  // 🔴 Read for a PICKED buyer too, not only a locked one. The listing picker
  // seeds itself from this buyer's budget and target cash flow, and the summary
  // row a picker hands back carries neither — so the standalone entry point,
  // which is exactly where a specialist has the least context, would be the one
  // place the seeding did not happen.
  const effectiveBuyerId = buyerLocked ? buyerLeadId! : (pickedBuyer?.id ?? null);
  const effectiveListingId = listingLocked ? listingId! : (pickedListing?.id ?? null);

  const prefilledBuyer = useQuery({
    queryKey: buyerKeys.detail(effectiveBuyerId ?? ""),
    queryFn: () => buyersApi.get(effectiveBuyerId!),
    enabled: open && Boolean(effectiveBuyerId),
  });
  const prefilledListing = useQuery({
    queryKey: listingKeys.detail(listingId ?? ""),
    queryFn: () => listingsApi.get(listingId!),
    enabled: open && listingLocked,
  });

  const buyerDisplay = buyerLocked
    ? (prefilledBuyer.data?.displayName ?? buyerName ?? "This buyer")
    : (pickedBuyer?.displayName ?? null);
  // SHORT — this lands in a `truncate` div in the wizard's summary rail, where
  // the whole marketplace title is ellipsed away to nothing useful anyway. The
  // `listingName` prop the callers pass is already shortened by `listingTitle`.
  const listingDisplay = listingLocked
    ? (resolvedShortListingName(
        prefilledListing.data?.displayName,
        prefilledListing.data?.businessName,
      ) ??
      listingName ??
      "This listing")
    : resolvedShortListingName(pickedListing?.displayName, pickedListing?.businessName);

  const chosenAskingPrice = listingLocked
    ? prefilledListing.data?.askingPrice
    : pickedListing?.askingPrice;

  // 🔴 The server's own refusal, mirrored — not an ownership rule re-derived
  // here. A locked buyer the caller does not own would 403 on create, and the
  // buyer step that would normally explain it is not drawn.
  const ownershipBlocked = buyerLocked && prefilledBuyer.data?.canEdit === false;

  // ---- The presentation check (R1) and the prequalification read ----------
  const duplicates = useQuery({
    queryKey: buyerSubmissionKeys.duplicateCheck(
      effectiveBuyerId ?? "",
      effectiveListingId ?? "",
    ),
    queryFn: () =>
      buyerSubmissionsApi.duplicateCheck({
        buyerLeadId: effectiveBuyerId!,
        listingId: effectiveListingId!,
      }),
    enabled: open && Boolean(effectiveBuyerId && effectiveListingId),
  });
  // 🔴 `hasMatches` now means REFUSED. The first match is the standing one
  // (furthest along first). A 409 from create is the same refusal arriving late,
  // and counts only for the pair it was about — choosing another pair drops it.
  const standing = duplicates.data?.hasMatches ? duplicates.data.matches[0] : undefined;
  const refusal: BuyerSubmissionPresentationDto | null =
    conflict &&
    conflict.buyerLeadId === effectiveBuyerId &&
    conflict.listingId === effectiveListingId
      ? conflict
      : standing && effectiveBuyerId && effectiveListingId
        ? {
            submissionId: standing.id,
            buyerLeadId: effectiveBuyerId,
            listingId: effectiveListingId,
            status: standing.status,
            isArchived: standing.isArchived ?? false,
            archivedAtUtc: standing.archivedAtUtc ?? null,
          }
        : null;

  const prequalification = useQuery({
    queryKey: buyerKeys.prequalification(effectiveBuyerId ?? ""),
    queryFn: () => buyersApi.getPrequalification(effectiveBuyerId!),
    enabled: open && Boolean(effectiveBuyerId),
  });

  // ---- The create-a-listing branch ---------------------------------------
  const listingNumberErrs = listingNumberErrors(listingForm);
  const agentErrs = agentFormErrors(agent);
  const canSaveListing =
    Object.keys(listingNumberErrs).length === 0 &&
    (!hasAgent || (agentHasIdentity(agent) && !agentErrs.email && !agentErrs.phone));

  const listingDuplicates = useListingDuplicates({
    rawUrl: listingForm.rawUrl,
    businessName: listingForm.businessName,
    enabled: open && stepKey === "listing-review",
  });
  const listingMatches = listingDuplicates.data?.matches ?? [];

  const extract = useMutation({
    mutationFn: () => listingsApi.pasteExtract(pasteText.trim()),
    onSuccess: (result) => {
      setListingForm({ ...listingFormFrom(result), notes: result.notesSummary ?? "" });
      setAgent(agentFormFrom(result.agent));
      setHasAgent(result.agent != null);
      setModelUsed(result.modelUsed ?? null);
      setExtractError(null);
      setFallbackNote(null);
      setStep(listingStepIndex + 2);
    },
    onError: (err: unknown) => {
      // A 422 means the text could not be read, or the daily AI ceiling was
      // reached — the server's own message, because those read completely
      // differently to the person waiting on it. Either way the paste is not
      // lost: drop into an empty form to fill in by hand.
      if (err instanceof ApiError && err.status === 422) {
        setListingForm(EMPTY_LISTING_FORM);
        setAgent(EMPTY_AGENT_FORM);
        setHasAgent(false);
        setModelUsed(null);
        setFallbackNote(
          err.message.trim() ||
            "We couldn't read that automatically — add the details below; your pasted text is kept.",
        );
        setStep(listingStepIndex + 2);
      } else {
        setExtractError(extractErrorMessage(err));
      }
    },
    // no-invalidation: reads a pasted blob into a prefill and persists nothing.
  });

  const saveListing = useMutation({
    mutationFn: () => {
      const body: CreateListingPayload = listingToPayload(listingForm, { forEdit: false });
      if (hasAgent && agentHasIdentity(agent)) {
        body.newAgent = agentToPayload(agent, { forEdit: false });
      }
      if (listingMatches.length > 0) body.confirmCreateDespiteDuplicate = true;
      return listingsApi.create(body);
    },
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: listingKeys.all });
      toast.success("Listing created.");
      // Collapse the branch back onto the Listing step with the new listing
      // chosen, rather than advancing past it: the specialist sees what was
      // saved, and Back can never land on a step that would create a second one.
      setPickedListing(created);
      setCreatingListing(false);
      setStep(listingStepIndex);
    },
    onError: (e: unknown) => setError(extractErrorMessage(e)),
  });

  // ---- The submission ----------------------------------------------------
  /** Everything a presentation (or a restore, which brings one back) moves. */
  const invalidatePresentationCaches = () => {
    void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
    // Creating a submission moves the buyer to ActivelySubmitting, bumps its
    // submission count and derived furthest-along stage, and is what the
    // shortlist's status badge is derived from — so the buyer caches are
    // stale too.
    void qc.invalidateQueries({ queryKey: buyerKeys.all });
    // And the ranking: a listing this buyer has now been presented to drops
    // out of Suggested. That prefix is rooted on `["listings"]`, so neither
    // invalidation above reaches it.
    void qc.invalidateQueries({ queryKey: listingMatchKeys.all });
  };

  /** Close, and show the submission. Shared by create and restore so both land
   *  the specialist in the same place. */
  const landOn = (submissionId: string) => {
    onClose();
    // 🔴 The host shows it in place when it can. Navigating unmounts whatever
    // the specialist was standing on — a buyer's record, a listing's, and any
    // drawer open over it — to show a record that could have been opened over
    // the top of it. The navigate stays as the fallback for the one mount
    // point with nothing underneath.
    if (onCreated) onCreated(submissionId);
    else navigate(`/buyer-submissions/${submissionId}`);
  };

  const create = useMutation({
    mutationFn: (body: CreateBuyerSubmissionPayload) => buyerSubmissionsApi.create(body),
    onSuccess: (created) => {
      invalidatePresentationCaches();
      toast.success("Buyer presented.");
      landOn(created.id);
    },
    onError: (e: unknown) => {
      // The race backstop: presented between the check and the click. Shown as
      // the same refusal, never as an error toast to dismiss and retry.
      const existing = submissionAlreadyPresented(e);
      if (existing) {
        setConflict(existing);
        void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
        return;
      }
      setError(extractErrorMessage(e));
    },
  });

  // D12 — restore acts at once with an UNDO that re-archives.
  const rearchive = useArchiveSubmission();
  const restore = useMutation({
    mutationFn: (submissionId: string) => buyerSubmissionsApi.restore(submissionId),
    onSuccess: (restored) => {
      invalidatePresentationCaches();
      toast.success("Restored.", {
        durationMs: 8000,
        action: { label: "Undo", onClick: () => rearchive.mutate(restored.id) },
      });
      landOn(restored.id);
    },
    onError: (e: unknown) => setError(extractErrorMessage(e)),
  });

  const submit = () => {
    if (!effectiveBuyerId || !effectiveListingId || refusal) return;
    setError(null);
    // 🔴 No override flag, ever (R1): a presented pair is refused, not confirmed.
    create.mutate({
      buyerLeadId: effectiveBuyerId,
      listingId: effectiveListingId,
      askingPriceAtSubmission: parseMoney(askingPrice),
      notes: notes.trim() || null,
      nextFollowUpDate: calendarDateInputToIso(followUp),
    });
  };

  // ---- Navigation --------------------------------------------------------
  const enterCreateListing = () => {
    setCreatingListing(true);
    setStep(listingStepIndex + 1);
  };

  const changeListing = () => {
    setListingUnlocked(true);
    setPickedListing(null);
    // The ladder gains the Listing step at this index; nothing already typed on
    // Terms is touched.
    setStep(listingStepIndex);
  };

  const goBack = () => {
    if (stepKey === "paste") {
      // Leaving the branch collapses its two steps, so they are never drawn
      // while unreachable.
      setCreatingListing(false);
      setStep(listingStepIndex);
      return;
    }
    setStep((s) => Math.max(s - 1, 0));
  };

  const stepValid = (() => {
    switch (stepKey) {
      case "buyer":
        return pickedBuyer != null && pickedBuyer.canEdit;
      case "listing":
        return pickedListing != null;
      case "nda":
        return true;
      case "terms":
        return Boolean(effectiveBuyerId && effectiveListingId) && !ownershipBlocked;
      default:
        return true;
    }
  })();

  const submitting =
    create.isPending || saveListing.isPending || extract.isPending || restore.isPending;

  /**
   * The primary action, per step.
   *
   * "Continue" only where a further step follows; otherwise the step's own
   * terminal verb — the NewDocumentRequestModal flip, so the button never
   * promises a screen that is not coming.
   */
  const primary: { label: string; run: () => void; disabled: boolean } | null = (() => {
    switch (stepKey) {
      case "paste":
        return {
          label: extract.isPending ? "Extracting…" : "Extract",
          run: () => extract.mutate(),
          disabled: extract.isPending || pasteText.trim().length === 0,
        };
      case "listing-review":
        return {
          label: saveListing.isPending
            ? "Saving…"
            : listingMatches.length > 0
              ? "Create anyway"
              : "Create listing",
          run: () => {
            setError(null);
            saveListing.mutate();
          },
          disabled: saveListing.isPending || !canSaveListing,
        };
      // 🔴 A presented pair is offered NO way forward through this wizard
      // (R1) — the refusal's own RESTORE / OPEN SUBMISSION is the way forward.
      case "terms":
        if (refusal) return null;
        return {
          label: create.isPending ? "Presenting…" : "Present buyer",
          run: submit,
          // Held while the check is in flight: Present on a pair that is about to
          // be refused would be a click the server undoes.
          disabled: create.isPending || duplicates.isLoading || !stepValid,
        };
      default:
        if (refusal) return null;
        return {
          label: "Continue",
          run: () => setStep((s) => Math.min(s + 1, steps.length - 1)),
          disabled: !stepValid,
        };
    }
  })();

  return (
    <Modal
      open={open}
      onClose={() => (submitting ? undefined : onClose())}
      title="Present a buyer to a listing"
      description="A submission is the pairing of one buyer with one listing — this walks through it."
      size="lg"
      dismissOnBackdropClick={false}
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button
            variant="text"
            onClick={onClose}
            disabled={submitting}
            data-testid="present-cancel"
          >
            Cancel
          </Button>
          {step > 0 && (
            <Button
              variant="secondary"
              onClick={goBack}
              disabled={submitting}
              data-testid="present-back"
            >
              Back
            </Button>
          )}
          {primary && (
            <Button
              onClick={primary.run}
              disabled={primary.disabled}
              data-testid="present-primary"
            >
              {primary.label}
            </Button>
          )}
        </div>
      }
    >
      <div className="grid gap-space-4" data-testid="present-buyer-wizard">
        <StatusStepper
          steps={steps}
          currentIndex={Math.min(step, steps.length - 1)}
          label="Present a buyer steps"
          data-testid="present-wizard-stepper"
        />

        {(buyerLocked || listingLocked) && (
          <LaunchContext
            buyerDisplay={buyerLocked ? buyerDisplay : null}
            listingDisplay={listingLocked ? listingDisplay : null}
            onChangeListing={listingLocked ? changeListing : undefined}
          />
        )}

        {ownershipBlocked && (
          <div
            className="rounded-md border border-status-error bg-white-warm p-space-3"
            role="alert"
            data-testid="present-ownership-blocked"
          >
            <p className="text-small text-onyx m-0">
              This buyer is assigned to{" "}
              {prefilledBuyer.data?.assignedSpecialistName ?? "another consultant"}, so a
              submission on them would be refused. Ask a Buyer Manager to reassign it.
            </p>
          </div>
        )}

        {refusal && (
          <AlreadyPresented
            refusal={refusal}
            archivedOn={refusal.isArchived ? fmt.date(refusal.archivedAtUtc) : null}
            restoring={restore.isPending}
            onRestore={() => {
              setError(null);
              restore.mutate(refusal.submissionId);
            }}
            onOpen={onClose}
          />
        )}

        {/* ---- Buyer ---- */}
        {stepKey === "buyer" && (
          <div className="grid gap-space-3" data-testid="present-step-buyer">
            <StepBlurb lead={WHAT_A_SUBMISSION_IS} body={BUYER_STEP_BLURB} />
            <BuyerPicker
              selected={pickedBuyer}
              onSelect={setPickedBuyer}
              onClear={() => setPickedBuyer(null)}
              enabled={open}
              listingId={effectiveListingId}
            />
          </div>
        )}

        {/* ---- Listing ---- */}
        {stepKey === "listing" && (
          <div className="grid gap-space-3" data-testid="present-step-listing">
            <StepBlurb
              lead={buyerLocked ? WHAT_A_SUBMISSION_IS : undefined}
              body={LISTING_STEP_BLURB}
            />
            <ListingPicker
              selected={pickedListing}
              onSelect={setPickedListing}
              onClear={() => setPickedListing(null)}
              onCreateNew={enterCreateListing}
              enabled={open}
              buyerLeadId={effectiveBuyerId}
              buyerName={buyerDisplay}
              /* 🔴 The plumbing that already existed and had never been used.
                 `ListingListQuery` has carried `askingPriceMax` and
                 `cashFlowMin` since the catalog shipped, and nothing in the
                 product had ever sent them — so a specialist with the buyer's
                 budget on the screen behind them still searched the whole
                 catalog by hand. */
              criteria={{
                maxPurchasePrice: prefilledBuyer.data?.maxPurchasePrice,
                targetCashFlowMin: prefilledBuyer.data?.targetCashFlowMin,
              }}
            />
          </div>
        )}

        {/* ---- Create a listing: paste ---- */}
        {stepKey === "paste" && (
          <div className="grid gap-space-3" data-testid="present-step-paste">
            <StepBlurb body={PASTE_STEP_BLURB} />
            <Textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              rows={10}
              placeholder="Paste here…"
              aria-label="Paste a listing to extract"
              data-testid="present-paste-text"
            />
            <p className="text-caption text-gray-mid m-0 flex items-center gap-space-1">
              <Sparkles size={13} strokeWidth={1.5} aria-hidden />
              Read by AI into a prefill you check — nothing is saved by extracting.
            </p>
            {extractError && (
              <p className="text-caption text-status-error m-0" role="alert">
                {extractError}
              </p>
            )}
          </div>
        )}

        {/* ---- Create a listing: review ---- */}
        {stepKey === "listing-review" && (
          <div className="grid gap-space-4" data-testid="present-step-listing-review">
            <StepBlurb body={LISTING_REVIEW_BLURB} />

            {fallbackNote && (
              <div
                className="rounded-md border border-status-info bg-white-warm px-space-3 py-space-2 text-caption text-onyx"
                role="status"
              >
                {fallbackNote}
              </div>
            )}

            {listingMatches.length > 0 && (
              <ListingDuplicateWarning
                matches={listingMatches}
                normalizedUrl={listingDuplicates.data?.normalizedUrl}
              />
            )}

            <ListingFieldsBlock
              value={listingForm}
              onChange={setListingForm}
              errors={listingNumberErrs}
            />

            <div className="grid gap-space-3 border-t border-border-light pt-space-4">
              <div className="flex items-center justify-between gap-space-3">
                <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0">
                  Listing agent
                </h3>
                {!hasAgent && (
                  <Button variant="text" size="sm" onClick={() => setHasAgent(true)}>
                    Add an agent
                  </Button>
                )}
              </div>
              {hasAgent ? (
                <>
                  <p className="text-caption text-gray-mid m-0">
                    Matched by email address — if this agent is already on file we&apos;ll
                    attach to that record rather than creating a second one.
                  </p>
                  <AgentFieldsBlock value={agent} onChange={setAgent} errors={agentErrs} />
                  <div>
                    <Button variant="text" size="sm" onClick={() => setHasAgent(false)}>
                      Save without an agent
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-caption text-gray-mid m-0">
                  No agent found in the pasted text. A listing without one can still be
                  worked — it just has no named contact on the seller&apos;s side.
                </p>
              )}
            </div>

            {/* 🔴 The MODEL is not the point and naming it is not a courtesy —
                "claude-opus-5" tells a specialist nothing they can act
                on and reads as a system leak. What matters is the provenance:
                a machine proposed this from pasted text, and they are the one
                saving it. */}
            {modelUsed && (
              <p className="text-caption text-gray-mid m-0">
                Extracted automatically from pasted text.
              </p>
            )}
          </div>
        )}

        {/* ---- NDA authorisation ---- */}
        {stepKey === "nda" && (
          <NdaAuthorisationStep
            buyerLeadId={effectiveBuyerId}
            linked={Boolean(prequalification.data?.submissionId)}
            reference={prequalification.data?.referenceNumber ?? null}
            loading={prequalification.isLoading}
            failed={prequalification.isError}
            onRetry={() => void prequalification.refetch()}
            retrying={prequalification.isFetching}
          />
        )}

        {/* ---- Terms ---- */}
        {stepKey === "terms" && (
          <div className="grid gap-space-4" data-testid="present-step-terms">
            <StepBlurb body={TERMS_STEP_BLURB} />

            {duplicates.isLoading && (
              <Skeleton className="h-16 w-full" data-testid="submission-duplicate-loading" />
            )}

            {/* 🔴 WARN, NEVER BLOCK — the same posture as the NDA step before
                it. A buyer who has not been educated can still be
                presented: a hard gate would strand a live deal over a process
                step, and the server refuses nothing here.

                Reads the buyer detail this wizard has ALREADY loaded for its
                own display name, so it costs no request. Rendered only once
                that read has landed — an absent stamp on an in-flight query
                would claim the buyer is uneducated before we know. */}
            {prefilledBuyer.data && !prefilledBuyer.data.educationSentAtUtc && (
              <div
                className="rounded-md border border-status-warning bg-white-warm p-space-3"
                data-testid="present-education-warning"
              >
                <div className="flex items-start gap-space-2">
                  <AlertTriangle
                    className="h-4 w-4 text-status-warning mt-[2px] shrink-0"
                    aria-hidden
                  />
                  <p className="text-small text-onyx m-0">
                    This buyer has not been sent the education email, so they may not be
                    able to explain how the financing works when the broker asks. You can
                    present them anyway — send it from the buyer's{" "}
                    <Link
                      to={`/buyers/${effectiveBuyerId}?tab=qualification`}
                      className="text-gold-shadow hover:underline"
                      target="_blank"
                      rel="noopener"
                    >
                      Qualification tab
                    </Link>
                    .
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-4">
              <div className="space-y-space-1">
                <label
                  htmlFor="submission-asking-price"
                  className="block text-caption uppercase tracking-wide text-gray-mid"
                >
                  Asking price at submission
                </label>
                <Input
                  id="submission-asking-price"
                  data-testid="submission-asking-price"
                  inputMode="decimal"
                  value={askingPrice}
                  onChange={(e) => setAskingPrice(e.target.value)}
                  placeholder={
                    chosenAskingPrice != null
                      ? formatCurrency(chosenAskingPrice)
                      : "The listing's current price"
                  }
                />
                <p className="text-caption text-gray-mid m-0">{PRICE_SNAPSHOT_BLURB}</p>
              </div>

              <div className="space-y-space-1">
                <label
                  htmlFor="submission-follow-up"
                  className="block text-caption uppercase tracking-wide text-gray-mid"
                >
                  Next follow-up (optional)
                </label>
                <Input
                  id="submission-follow-up"
                  data-testid="submission-follow-up"
                  type="date"
                  value={followUp}
                  onChange={(e) => setFollowUp(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-space-1">
              <label
                htmlFor="submission-notes"
                className="block text-caption uppercase tracking-wide text-gray-mid"
              >
                Notes (optional)
              </label>
              <Textarea
                id="submission-notes"
                data-testid="submission-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything worth recording about why this pairing"
              />
            </div>

            <p className="text-caption text-gray-mid m-0" data-testid="present-what-next">
              {WHAT_HAPPENS_NEXT}
            </p>
          </div>
        )}

        {error && (
          <p className="text-small text-status-error m-0" role="alert" data-testid="present-error">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * What arrived with the launch context.
 *
 * <p>🔴 <b>The buyer chip is a LABEL, never a link.</b> A control that discards
 * a half-filled form is a bug wearing a different hat, and the buyer is the
 * record the specialist opened this from — changing it would mean they are on
 * the wrong page, not that they want a different pairing.</p>
 *
 * <p>The listing gets an escape hatch because it arrives as a SUGGESTION — the
 * row that was pressed — and changing it discards only that one choice.</p>
 */
function LaunchContext({
  buyerDisplay,
  listingDisplay,
  onChangeListing,
}: {
  buyerDisplay: string | null;
  listingDisplay: string | null;
  onChangeListing?: () => void;
}) {
  return (
    <div
      className="grid gap-space-2 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
      data-testid="present-launch-context"
    >
      {buyerDisplay && (
        <div data-testid="present-locked-buyer">
          <div className="font-display text-caption uppercase tracking-label text-gray-mid">
            Buyer
          </div>
          <div className="text-small text-onyx">{buyerDisplay}</div>
        </div>
      )}
      {listingDisplay && (
        <div className="flex items-end justify-between gap-space-3">
          <div className="min-w-0" data-testid="present-locked-listing">
            <div className="font-display text-caption uppercase tracking-label text-gray-mid">
              Listing
            </div>
            <div className="text-small text-onyx truncate">{listingDisplay}</div>
          </div>
          {onChangeListing && (
            <button
              type="button"
              onClick={onChangeListing}
              className="shrink-0 text-caption uppercase tracking-wide text-gold-shadow hover:underline"
              data-testid="present-change-listing"
            >
              Change listing
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The refusal (ruling R1): this buyer is already presented to this listing.
 *
 * <p>🔴 <b>It names the standing submission and offers ONE way forward</b> —
 * RESTORE an archived one (which lands the specialist on it, as a create
 * would), OPEN any other. There is no "present anyway": to retry a dead
 * pairing, staff reopen the existing submission with Change status.</p>
 */
function AlreadyPresented({
  refusal,
  archivedOn,
  restoring,
  onRestore,
  onOpen,
}: {
  refusal: BuyerSubmissionPresentationDto;
  archivedOn: string | null;
  restoring: boolean;
  onRestore: () => void;
  onOpen: () => void;
}) {
  const hint = alreadyPresentedHint(refusal);
  return (
    <div
      className="rounded-md border border-status-warning bg-white-warm p-space-3"
      role="alert"
      data-testid="present-already-presented"
    >
      <div className="flex items-start gap-space-2">
        <AlertTriangle className="h-4 w-4 text-status-warning mt-[2px] shrink-0" aria-hidden />
        <div className="grid gap-space-2">
          <p className="text-small font-semibold text-onyx m-0" data-testid="present-already-presented-reason">
            {alreadyPresentedReason(refusal, archivedOn)}
          </p>
          <p className="text-caption text-gray-mid m-0">{ALREADY_PRESENTED_BLURB}</p>
          {hint && <p className="text-caption text-gray-mid m-0">{hint}</p>}
          <div>
            {refusal.isArchived ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={onRestore}
                disabled={restoring}
                data-testid="present-already-presented-restore"
              >
                {restoring ? "Restoring…" : "Restore"}
              </Button>
            ) : (
              <Link
                to={`/buyer-submissions/${refusal.submissionId}`}
                onClick={onOpen}
                className={buttonVariants({ variant: "secondary", size: "sm" })}
                data-testid="present-already-presented-open"
              >
                Open submission
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Where this buyer stands on authorising Phoenix to sign the broker's NDA.
 *
 * <p>🔴 <b>Explanatory, never a gate.</b> The server's precondition is on
 * recording a SIGNED NDA, and create has no such precondition — so this step
 * teaches and then gets out of the way. The point is that the specialist learns
 * it here rather than as a submit-time toast with a listing broker waiting.</p>
 */
function NdaAuthorisationStep({
  buyerLeadId,
  linked,
  reference,
  loading,
  failed,
  onRetry,
  retrying,
}: {
  buyerLeadId: string | null;
  linked: boolean;
  reference: string | null;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  retrying: boolean;
}) {
  return (
    <div className="grid gap-space-3" data-testid="present-step-nda">
      <StepBlurb body={NDA_STEP_BLURB} />

      {loading ? (
        <Skeleton className="h-24 w-full" />
      ) : failed ? (
        // "This buyer has not authorised it" is a claim about their record, and
        // a failed read is no evidence for it — the worse direction of the two,
        // because it sends a specialist off to re-run a funnel already done.
        <InlineQueryError
          what="this buyer's prequalification"
          onRetry={onRetry}
          retrying={retrying}
        />
      ) : linked ? (
        <div
          className="rounded-md border border-border-light bg-white-warm p-space-3 grid gap-space-2"
          data-testid="present-nda-authorised"
        >
          <StatusBadge tone="ok">Authorised</StatusBadge>
          <p className="text-small text-onyx m-0">
            {NDA_AUTHORISED}
            {reference ? ` Prequalification ${reference}.` : ""}
          </p>
        </div>
      ) : (
        <div
          className="rounded-md border border-status-warning bg-white-warm p-space-3 grid gap-space-3"
          data-testid="present-nda-not-authorised"
        >
          <StatusBadge tone="warn">Not authorised yet</StatusBadge>
          <p className="text-small text-onyx m-0">{NDA_NOT_AUTHORISED}</p>

          {/* 🔴 Resolved IN PLACE. See NDA_SEND_INTRO for the two ways the link
              that used to sit here failed. */}
          {buyerLeadId && <SendPrequalificationInvite buyerLeadId={buyerLeadId} />}

          <ul className="grid gap-space-2 list-none p-0 m-0">
            {NDA_EXITS.map((exit) => (
              <li key={exit.label}>
                <div className="text-small text-onyx">{exit.label}</div>
                <div className="text-caption text-gray-mid">{exit.detail}</div>
              </li>
            ))}
          </ul>
          <p className="text-caption text-gray-mid m-0">{NDA_DOES_NOT_BLOCK}</p>
        </div>
      )}
    </div>
  );
}

/**
 * Send this buyer the prequalification invitation, from inside the wizard.
 *
 * <p>🔴 <b>One click, with what it does said before the button rather than
 * behind a confirm.</b> A buyer's address is already on their own record, so
 * there is nothing to type and nothing to verify — which is exactly the
 * reasoning that keeps the buyer's own Prequalification tab one click while the
 * SELLER invitation goes through a preview (that module holds no seller contact
 * record, so the address must be asked for). Putting a confirm here would add a
 * second click in front of the one action this step exists to unblock.</p>
 *
 * <p>The duplicate refusal IS a confirm, and it is the shared one — a live
 * invitation already exists, and a sender may genuinely want a second, but
 * never by accident. Rendering `DuplicateInviteConfirm` rather than wording it
 * again keeps the sentence a sender reads written once across all three
 * surfaces.</p>
 */
function SendPrequalificationInvite({ buyerLeadId }: { buyerLeadId: string }) {
  const [duplicate, setDuplicate] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = useSendBuyerPrequalInvite(buyerLeadId);

  async function run(confirmDuplicate: boolean) {
    setError(null);
    try {
      await send.mutateAsync({ confirmDuplicate });
      setSent(true);
      setDuplicate(false);
    } catch (err: unknown) {
      if ((err as { status?: number })?.status === 409) {
        setDuplicate(true);
        return;
      }
      setError(extractErrorMessage(err));
    }
  }

  if (sent) {
    return (
      <p
        className="text-small text-status-success m-0"
        role="status"
        data-testid="present-nda-invite-sent"
      >
        {NDA_SEND_SENT}
      </p>
    );
  }

  return (
    <div className="grid gap-space-2" data-testid="present-nda-invite">
      <p className="text-small text-onyx m-0">{NDA_SEND_INTRO}</p>
      <p className="text-caption text-gray-mid m-0">{NDA_SEND_CONFIRM}</p>

      {duplicate ? (
        <DuplicateInviteConfirm
          testId="present-nda-invite-duplicate"
          subject="This buyer"
          pending={send.isPending}
          onConfirm={() => void run(true)}
          onCancel={() => setDuplicate(false)}
        />
      ) : (
        <div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void run(false)}
            disabled={send.isPending}
            data-testid="present-nda-invite-send"
          >
            {send.isPending ? "Sending…" : "Send the prequalification invitation"}
          </Button>
        </div>
      )}

      {error && (
        <p className="text-caption text-status-error m-0" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** One step's plain explanation. Every step carries one — a wizard that only
 *  asks is a form split across screens. */
function StepBlurb({ lead, body }: { lead?: string; body: string }) {
  return (
    <div className="grid gap-space-1">
      {lead && <p className="text-small text-onyx m-0">{lead}</p>}
      <p className="text-small text-gray-mid m-0">{body}</p>
    </div>
  );
}

/** Blank means "unchanged / let the server decide", never zero. */
function parseMoney(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : null;
}
